import React, { useState, useEffect, useRef } from 'react';
import {
	ArrowUpIcon,
	ArrowRightIcon,
	CheckCircleIcon,
	ExclamationTriangleIcon,
	PencilSquareIcon,
	BookmarkIcon,
} from '@heroicons/react/24/outline';

import {
	publishSop,
	getSopLatestVersion,
	getEsignatures,
	createEsignature,
} from '../api';
import { StackableModal } from '../components/nextgen';
import { classNames } from '../components/helpers/functions';
import SignaturePad from '../components/SignaturePad';

import useModal from '../hooks/useModal';

const MODAL_NAME = 'publishSop';

// E-Signature Terms and Conditions
const ESIGNATURE_TERMS = `By creating and using this electronic signature, I agree that:

1. My electronic signature is the legal equivalent of my handwritten signature.
2. I consent to use electronic signatures for signing documents in this system.
3. I am the authorized user of this account and am responsible for all signatures created.
4. I understand that my signature will be recorded with a timestamp and linked to my account.
5. This signature may be used for compliance and audit purposes.`;

// Helper to format version string
const formatVersion = (major, minor) => {
	return `${major || 0}.${minor || 0}`;
};

// Helper to get next version based on type
const getNextVersion = (currentMajor, currentMinor, type) => {
	if (type === 'major') {
		return { major: (currentMajor || 0) + 1, minor: 0 };
	}
	return { major: currentMajor || 0, minor: (currentMinor || 0) + 1 };
};

// Strip HTML tags for text comparison
const stripHtml = (html) => {
	if (!html) return '';
	return html.replace(/<[^>]*>/g, '').trim();
};

// Get first line preview
const getFirstLine = (html) => {
	if (!html) return '';
	const text = html
		.replace(/<\/?(p|div|br|h[1-6])[^>]*>/gi, '\n')
		.replace(/<[^>]*>/g, '')
		.trim();
	const firstLine = text.split('\n').find((line) => line.trim()) || '';
	const truncated = firstLine.trim().slice(0, 60);
	return truncated + (firstLine.length > 60 ? '...' : '');
};

const VERSION_TYPES = [
	{
		value: 'minor',
		label: 'Minor Version',
		description: 'Small changes, bug fixes, or minor updates',
		icon: ArrowRightIcon,
	},
	{
		value: 'major',
		label: 'Major Version',
		description: 'Significant changes, new features, or breaking changes',
		icon: ArrowUpIcon,
	},
];

const PublishSopModal = () => {
	const { open, setClose } = useModal(MODAL_NAME);
	const signaturePadRef = useRef(null);

	const [versionType, setVersionType] = useState('minor');
	const [isPublishing, setIsPublishing] = useState(false);
	const [lastVersion, setLastVersion] = useState(null);
	const [loading, setLoading] = useState(true);

	// E-Signature state
	const [savedSignatures, setSavedSignatures] = useState([]);
	const [loadingSignatures, setLoadingSignatures] = useState(true);
	const [signatureMode, setSignatureMode] = useState('saved'); // 'saved' or 'new'
	const [selectedSignatureId, setSelectedSignatureId] = useState(null);
	const [newSignatureData, setNewSignatureData] = useState(null);
	const [newSignatureName, setNewSignatureName] = useState('');
	const [saveNewSignature, setSaveNewSignature] = useState(false);
	const [termsAccepted, setTermsAccepted] = useState(false);
	const [showTerms, setShowTerms] = useState(false);

	const sop = open?.sop;
	const currentMajor = sop?.versionMajor || 0;
	const currentMinor = sop?.versionMinor || 0;
	const isFirstPublish = currentMajor === 0 && currentMinor === 0;

	// Fetch saved signatures
	useEffect(() => {
		const fetchSignatures = async () => {
			setLoadingSignatures(true);
			try {
				const signatures = await getEsignatures();
				setSavedSignatures(signatures || []);
				// Auto-select default signature if exists
				const defaultSig = signatures?.find((s) => s.isDefault);
				if (defaultSig) {
					setSelectedSignatureId(defaultSig.id);
				}
			} catch (error) {
				console.error('Failed to fetch signatures:', error);
				setSavedSignatures([]);
			} finally {
				setLoadingSignatures(false);
			}
		};

		if (open) {
			fetchSignatures();
		}
	}, [open]);

	// Fetch the last published version for comparison
	useEffect(() => {
		const fetchLastVersion = async () => {
			if (!sop?.id) return;

			setLoading(true);
			try {
				const version = await getSopLatestVersion({ id: sop.id });
				setLastVersion(version);
			} catch (error) {
				// No previous version exists
				setLastVersion(null);
			} finally {
				setLoading(false);
			}
		};

		if (open) {
			fetchLastVersion();
			setVersionType('minor');
		}
	}, [open, sop?.id]);

	const handleClose = () => {
		setVersionType('minor');
		setLastVersion(null);
		setSignatureMode('saved');
		setSelectedSignatureId(null);
		setNewSignatureData(null);
		setNewSignatureName('');
		setSaveNewSignature(false);
		setTermsAccepted(false);
		setShowTerms(false);
		setClose();
	};

	const handlePublish = async () => {
		if (!sop?.id) return;

		// Validate signature
		let signatureId = null;
		let signatureData = null;

		if (signatureMode === 'saved') {
			if (!selectedSignatureId) {
				alert('Please select a saved signature.');
				return;
			}
			signatureId = selectedSignatureId;
		} else {
			if (!newSignatureData) {
				alert('Please draw your signature.');
				return;
			}

			// If saving new signature, create it first
			if (saveNewSignature) {
				if (!termsAccepted) {
					alert('Please accept the terms and conditions to save your signature.');
					return;
				}
				if (!newSignatureName.trim()) {
					alert('Please enter a name for your signature.');
					return;
				}

				try {
					const newSig = await createEsignature({
						name: newSignatureName.trim(),
						signatureData: newSignatureData,
						termsAccepted: true,
					});
					signatureId = newSig.id;
				} catch (error) {
					console.error('Failed to save signature:', error);
					alert('Failed to save signature. Please try again.');
					return;
				}
			} else {
				// Use one-time signature data
				signatureData = newSignatureData;
			}
		}

		setIsPublishing(true);

		try {
			await publishSop({
				id: sop.id,
				versionType,
				signatureId,
				signatureData,
			});

			if (typeof open?.onSuccess === 'function') {
				open.onSuccess();
			}
			handleClose();
		} catch (error) {
			console.error('Failed to publish SOP:', error);
			alert('Failed to publish SOP. Please try again.');
		} finally {
			setIsPublishing(false);
		}
	};

	const nextVersion = getNextVersion(currentMajor, currentMinor, versionType);

	// Calculate changes for diff display
	const hasNameChange = lastVersion && sop?.name !== lastVersion.name;
	const hasDescriptionChange =
		lastVersion &&
		stripHtml(sop?.description) !== stripHtml(lastVersion.description);
	const hasCategoryChange =
		lastVersion && sop?.category !== lastVersion.category;
	const hasStepsChange =
		lastVersion &&
		JSON.stringify(sop?.steps) !== JSON.stringify(lastVersion.steps);

	const changeCount = [
		hasNameChange,
		hasDescriptionChange,
		hasCategoryChange,
		hasStepsChange,
	].filter(Boolean).length;

	// Check if signature is valid for publishing
	const hasValidSignature =
		(signatureMode === 'saved' && selectedSignatureId) ||
		(signatureMode === 'new' && newSignatureData);

	return (
		<StackableModal
			open={!!open}
			onClose={handleClose}
			title="Publish SOP Version"
			size="2xl"
		>
			<div className="space-y-6">
				{/* Version Selection */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-3">
						Version Type
					</label>
					<div className="grid grid-cols-2 gap-3">
						{VERSION_TYPES.map((type) => {
							const Icon = type.icon;
							const isSelected = versionType === type.value;
							const nextVer = getNextVersion(
								currentMajor,
								currentMinor,
								type.value,
							);

							return (
								<button
									key={type.value}
									type="button"
									onClick={() => setVersionType(type.value)}
									className={classNames(
										'relative flex flex-col items-start p-4 rounded-lg border-2 transition-all text-left',
										isSelected
											? 'border-indigo-500 bg-indigo-50'
											: 'border-gray-200 hover:border-gray-300',
									)}
								>
									<div className="flex items-center gap-2 mb-1">
										<Icon
											className={classNames(
												'h-5 w-5',
												isSelected ? 'text-indigo-600' : 'text-gray-400',
											)}
										/>
										<span
											className={classNames(
												'font-medium',
												isSelected ? 'text-indigo-900' : 'text-gray-900',
											)}
										>
											{type.label}
										</span>
									</div>
									<p className="text-xs text-gray-500 mb-2">
										{type.description}
									</p>
									<div
										className={classNames(
											'text-sm font-mono px-2 py-0.5 mt-2 rounded',
											isSelected
												? 'bg-indigo-100 text-indigo-700'
												: 'bg-gray-100 text-gray-600',
										)}
									>
										{formatVersion(currentMajor, currentMinor)} →{' '}
										{formatVersion(nextVer.major, nextVer.minor)}
									</div>
								</button>
							);
						})}
					</div>
				</div>

				{/* Changes Summary */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-3">
						Changes Summary
					</label>

					{loading ? (
						<div className="flex items-center justify-center py-6">
							<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
							<span className="ml-2 text-sm text-gray-500">Loading...</span>
						</div>
					) : isFirstPublish ? (
						<div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
							<div className="flex items-start gap-3">
								<CheckCircleIcon className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
								<div>
									<p className="text-sm font-medium text-blue-800">
										First Publication
									</p>
									<p className="text-sm text-blue-600 mt-1">
										This is the initial publication of this SOP. All content
										will be included in version{' '}
										{formatVersion(nextVersion.major, nextVersion.minor)}.
									</p>
								</div>
							</div>
						</div>
					) : (
						<div className="border border-gray-200 rounded-lg divide-y divide-gray-200">
							{/* Name change */}
							<div className="p-3 flex items-center justify-between">
								<span className="text-sm text-gray-700">Name</span>
								{hasNameChange ? (
									<div className="flex items-center gap-2 text-sm">
										<span className="text-red-600 line-through">
											{lastVersion.name}
										</span>
										<ArrowRightIcon className="h-3 w-3 text-gray-400" />
										<span className="text-green-600">{sop.name}</span>
									</div>
								) : (
									<span className="text-xs text-gray-400">No change</span>
								)}
							</div>

							{/* Category change */}
							<div className="p-3 flex items-center justify-between">
								<span className="text-sm text-gray-700">Category</span>
								{hasCategoryChange ? (
									<div className="flex items-center gap-2 text-sm">
										<span className="text-red-600 line-through capitalize">
											{lastVersion.category || 'None'}
										</span>
										<ArrowRightIcon className="h-3 w-3 text-gray-400" />
										<span className="text-green-600 capitalize">
											{sop.category || 'None'}
										</span>
									</div>
								) : (
									<span className="text-xs text-gray-400">No change</span>
								)}
							</div>

							{/* Description change */}
							<div className="p-3 flex items-center justify-between">
								<span className="text-sm text-gray-700">Description</span>
								{hasDescriptionChange ? (
									<span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
										Modified
									</span>
								) : (
									<span className="text-xs text-gray-400">No change</span>
								)}
							</div>

							{/* Steps changes */}
							<div className="p-3">
								<div className="flex items-center justify-between mb-2">
									<span className="text-sm text-gray-700">Steps</span>
									{hasStepsChange ? (
										<span className="inline-flex items-center rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800">
											Modified
										</span>
									) : (
										<span className="text-xs text-gray-400">No change</span>
									)}
								</div>
								{hasStepsChange && (
									<div className="mt-2 space-y-1">
										<div className="text-xs text-gray-500">
											{lastVersion.steps?.length || 0} steps →{' '}
											{sop.steps?.length || 0} steps
										</div>
										{sop.steps?.slice(0, 3).map((step, idx) => {
											const oldStep = lastVersion.steps?.[idx];
											const isChanged =
												!oldStep ||
												stripHtml(step.content) !== stripHtml(oldStep.content);
											return (
												<div
													key={idx}
													className={classNames(
														'text-xs px-2 py-1 rounded',
														isChanged ? 'bg-yellow-50' : 'bg-gray-50',
													)}
												>
													<span className="font-medium">Step {idx + 1}:</span>{' '}
													{getFirstLine(step.content) || (
														<em className="text-gray-400">Empty</em>
													)}
													{isChanged && !oldStep && (
														<span className="ml-1 text-green-600">(new)</span>
													)}
													{isChanged && oldStep && (
														<span className="ml-1 text-yellow-600">
															(modified)
														</span>
													)}
												</div>
											);
										})}
										{sop.steps?.length > 3 && (
											<div className="text-xs text-gray-400 px-2">
												+{sop.steps.length - 3} more steps
											</div>
										)}
									</div>
								)}
							</div>

							{/* Total changes */}
							{changeCount === 0 && (
								<div className="p-3 bg-yellow-50">
									<div className="flex items-start gap-2">
										<ExclamationTriangleIcon className="h-4 w-4 text-yellow-600 flex-shrink-0 mt-0.5" />
										<p className="text-xs text-yellow-700">
											No changes detected since the last published version. Are
											you sure you want to publish?
										</p>
									</div>
								</div>
							)}
						</div>
					)}
				</div>

				{/* E-Signature Section */}
				<div>
					<label className="block text-sm font-medium text-gray-700 mb-3">
						E-Signature <span className="text-red-500">*</span>
					</label>

					{/* Signature Mode Tabs */}
					<div className="flex border-b border-gray-200 mb-4">
						<button
							type="button"
							onClick={() => setSignatureMode('saved')}
							className={classNames(
								'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
								signatureMode === 'saved'
									? 'border-indigo-500 text-indigo-600'
									: 'border-transparent text-gray-500 hover:text-gray-700',
							)}
						>
							<BookmarkIcon className="h-4 w-4" />
							Saved Signatures
							{savedSignatures.length > 0 && (
								<span className="bg-gray-100 text-gray-600 text-xs px-1.5 py-0.5 rounded-full">
									{savedSignatures.length}
								</span>
							)}
						</button>
						<button
							type="button"
							onClick={() => setSignatureMode('new')}
							className={classNames(
								'flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
								signatureMode === 'new'
									? 'border-indigo-500 text-indigo-600'
									: 'border-transparent text-gray-500 hover:text-gray-700',
							)}
						>
							<PencilSquareIcon className="h-4 w-4" />
							Sign Now
						</button>
					</div>

					{/* Saved Signatures */}
					{signatureMode === 'saved' && (
						<div>
							{loadingSignatures ? (
								<div className="flex items-center justify-center py-6">
									<div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
									<span className="ml-2 text-sm text-gray-500">
										Loading signatures...
									</span>
								</div>
							) : savedSignatures.length === 0 ? (
								<div className="text-center py-6 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
									<PencilSquareIcon className="mx-auto h-10 w-10 text-gray-400" />
									<p className="mt-2 text-sm text-gray-600">
										No saved signatures yet
									</p>
									<button
										type="button"
										onClick={() => setSignatureMode('new')}
										className="mt-2 text-sm text-indigo-600 hover:text-indigo-500 font-medium"
									>
										Create your first signature
									</button>
								</div>
							) : (
								<div className="grid grid-cols-2 gap-3">
									{savedSignatures.map((sig) => (
										<button
											key={sig.id}
											type="button"
											onClick={() => setSelectedSignatureId(sig.id)}
											className={classNames(
												'relative p-3 rounded-lg border-2 transition-all text-left',
												selectedSignatureId === sig.id
													? 'border-indigo-500 bg-indigo-50'
													: 'border-gray-200 hover:border-gray-300',
											)}
										>
											{sig.isDefault && (
												<span className="absolute top-2 right-2 text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded">
													Default
												</span>
											)}
											<div className="mb-2">
												<img
													src={sig.signatureData}
													alt={sig.name}
													className="h-12 w-full object-contain"
												/>
											</div>
											<p className="text-sm font-medium text-gray-700 truncate">
												{sig.name}
											</p>
										</button>
									))}
								</div>
							)}
						</div>
					)}

					{/* New Signature */}
					{signatureMode === 'new' && (
						<div className="space-y-4">
							<SignaturePad
								ref={signaturePadRef}
								onSignatureChange={setNewSignatureData}
								width={500}
								height={150}
							/>

							{/* Save signature option */}
							<div className="space-y-3 p-4 bg-gray-50 rounded-lg">
								<div className="flex items-start gap-3">
									<input
										type="checkbox"
										checked={saveNewSignature}
										onChange={(e) => setSaveNewSignature(e.target.checked)}
										className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
									/>
									<div className="flex-1">
										<label
											className="text-sm font-medium text-gray-700 cursor-pointer"
											onClick={() => setSaveNewSignature(!saveNewSignature)}
										>
											Save this signature for future use
										</label>
										<p className="text-xs text-gray-500 mt-0.5">
											Your signature will be stored securely and can be reused
										</p>
									</div>
								</div>

								{saveNewSignature && (
									<>
										<div>
											<label className="block text-sm font-medium text-gray-700 mb-1">
												Signature Name
											</label>
											<input
												type="text"
												value={newSignatureName}
												onChange={(e) => setNewSignatureName(e.target.value)}
												placeholder="e.g., My Primary Signature"
												className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
											/>
										</div>

										<div className="border-t border-gray-200 pt-3">
											<div className="flex items-start gap-3">
												<input
													type="checkbox"
													checked={termsAccepted}
													onChange={(e) => setTermsAccepted(e.target.checked)}
													className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
												/>
												<div className="flex-1">
													<label
														className="text-sm text-gray-700 cursor-pointer"
														onClick={() => setTermsAccepted(!termsAccepted)}
													>
														I agree to the{' '}
														<button
															type="button"
															onClick={(e) => {
																e.stopPropagation();
																setShowTerms(!showTerms);
															}}
															className="text-indigo-600 hover:text-indigo-500 underline"
														>
															E-Signature Terms and Conditions
														</button>
													</label>
												</div>
											</div>

											{showTerms && (
												<div className="mt-3 p-3 bg-white rounded border border-gray-200 text-xs text-gray-600 whitespace-pre-line max-h-40 overflow-y-auto">
													{ESIGNATURE_TERMS}
												</div>
											)}
										</div>
									</>
								)}
							</div>
						</div>
					)}
				</div>

				{/* Version Preview */}
				<div className="bg-gray-50 rounded-lg p-4">
					<div className="flex items-center justify-between">
						<div>
							<p className="text-sm text-gray-500">New version will be</p>
							<p className="text-2xl font-bold text-gray-900 font-mono">
								v{formatVersion(nextVersion.major, nextVersion.minor)}
							</p>
						</div>
						<div className="text-right">
							<p className="text-sm text-gray-500">
								{isFirstPublish
									? 'Initial release'
									: `From v${formatVersion(currentMajor, currentMinor)}`}
							</p>
							<p className="text-xs text-gray-400">
								{versionType === 'major' ? 'Major release' : 'Minor release'}
							</p>
						</div>
					</div>
				</div>

				{/* Actions */}
				<div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
					<button
						type="button"
						onClick={handleClose}
						disabled={isPublishing}
						className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:opacity-50"
					>
						Cancel
					</button>
					<button
						type="button"
						onClick={handlePublish}
						disabled={isPublishing || loading || !hasValidSignature}
						className={classNames(
							'inline-flex items-center rounded-md px-4 py-2 text-sm font-medium text-white shadow-sm',
							isPublishing || loading || !hasValidSignature
								? 'bg-gray-400 cursor-not-allowed'
								: 'bg-green-600 hover:bg-green-700',
						)}
					>
						{isPublishing ? (
							<>
								<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
								Publishing...
							</>
						) : (
							<>
								<CheckCircleIcon className="h-4 w-4 mr-1.5" />
								Sign & Publish v
								{formatVersion(nextVersion.major, nextVersion.minor)}
							</>
						)}
					</button>
				</div>
			</div>
		</StackableModal>
	);
};

export default PublishSopModal;
